import { useMemo } from 'react';

// Intelligently distribute activities and restaurants across trip days
function buildItinerary({ destination, flight, hotel, activities, restaurants, startDate, endDate, nights }) {
  if (!destination || !startDate || !endDate || nights < 1) return [];

  const start = new Date(startDate + 'T12:00:00');
  const days = [];

  for (let i = 0; i <= nights; i++) {
    const date = new Date(start);
    date.setDate(date.getDate() + i);
    const dayNum = i + 1;
    const isFirst = i === 0;
    const isLast = i === nights;

    days.push({
      dayNum,
      date,
      dateStr: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
      isFirst,
      isLast,
      activities: [],
      restaurants: [],
      notes: [],
    });
  }

  // Day 1: arrival day — lighter schedule
  if (flight) {
    days[0].notes.push(`Arrive in ${destination.city} (${flight.flight_duration_outbound} flight)`);
  }
  if (hotel) {
    days[0].notes.push(`Check into ${hotel.hotel_name}`);
  }
  days[0].notes.push('Settle in, explore the neighborhood');

  // Last day: departure — light
  if (days.length > 1) {
    const last = days[days.length - 1];
    if (hotel) last.notes.push(`Check out of ${hotel.hotel_name}`);
    last.notes.push(`Depart ${destination.city}`);
  }

  // Distribute activities across middle days (not first or last)
  const middleDays = days.filter(d => !d.isFirst && !d.isLast);
  const activeDays = middleDays.length > 0 ? middleDays : days.filter(d => !d.isLast);

  // Full-day activities get their own day, half-day and shorter share days
  const fullDay = activities.filter(a => a.duration?.toLowerCase().includes('full'));
  const halfDay = activities.filter(a => a.duration?.toLowerCase().includes('half'));
  const shorter = activities.filter(a => !a.duration?.toLowerCase().includes('full') && !a.duration?.toLowerCase().includes('half'));

  let dayIdx = 0;
  // Place full-day activities first
  fullDay.forEach(a => {
    if (dayIdx < activeDays.length) {
      activeDays[dayIdx].activities.push(a);
      dayIdx++;
    }
  });

  // Place half-day activities (2 per day)
  halfDay.forEach(a => {
    // Find a day with space
    const target = activeDays.find(d => d.activities.length === 0) ||
                   activeDays.find(d => d.activities.length < 2 && !d.activities.some(x => x.duration?.toLowerCase().includes('full')));
    if (target) target.activities.push(a);
  });

  // Place shorter activities (up to 3 per day)
  shorter.forEach(a => {
    const target = activeDays.find(d => {
      const hasFullDay = d.activities.some(x => x.duration?.toLowerCase().includes('full'));
      if (hasFullDay) return d.activities.length < 2;
      return d.activities.length < 3;
    });
    if (target) target.activities.push(a);
    else if (activeDays.length > 0) {
      // Overflow to least-busy day
      const least = [...activeDays].sort((a, b) => a.activities.length - b.activities.length)[0];
      least.activities.push(a);
    }
  });

  // Distribute restaurants — 1 per day, starting with arrival day
  restaurants.forEach((r, i) => {
    const targetDay = days[i % days.length];
    targetDay.restaurants.push(r);
  });

  // Add a dinner note on arrival day if no restaurant picked
  if (days[0].restaurants.length === 0 && restaurants.length === 0) {
    days[0].notes.push('Find a spot for dinner near the hotel');
  }

  return days;
}

export default function DayByDay({ destination, flight, hotel, activities, restaurants, startDate, endDate, nights }) {
  const itinerary = useMemo(
    () => buildItinerary({ destination, flight, hotel, activities, restaurants, startDate, endDate, nights }),
    [destination, flight, hotel, activities, restaurants, startDate, endDate, nights]
  );

  if (itinerary.length === 0) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-lg text-white">Day-by-Day Itinerary</h3>
        <span className="text-xs text-brand-ink/55">{nights} nights &middot; {itinerary.length} days</span>
      </div>
      <div className="text-xs text-brand-ink/55 mb-2">
        Auto-built from your picks. Reorder as you like.
      </div>

      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-[19px] top-8 bottom-8 w-0.5 bg-gradient-to-b from-teal/40 via-teal/20 to-teal/40" />

        <div className="space-y-1">
          {itinerary.map((day, i) => (
            <div key={i} className="relative flex gap-4">
              {/* Day marker */}
              <div className="flex-shrink-0 w-10 flex flex-col items-center pt-1 z-10">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                  day.isFirst ? 'bg-teal text-navy' :
                  day.isLast ? 'bg-gold text-navy' :
                  'bg-navy-lighter border-2 border-teal/30 text-brand-ink/85'
                }`}>
                  {day.isFirst ? '✈' : day.isLast ? '✈' : day.dayNum}
                </div>
              </div>

              {/* Day content */}
              <div className="flex-1 bg-navy-light/50 rounded-xl border border-brand-ink/5 p-4 mb-2 hover:border-brand-ink/10 transition">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="font-semibold text-sm text-white">Day {day.dayNum}</span>
                    <span className="text-xs text-brand-ink/55 ml-2">{day.dateStr}</span>
                  </div>
                  {day.isFirst && <span className="text-[10px] bg-teal/20 text-teal px-2 py-0.5 rounded-full font-bold">ARRIVAL</span>}
                  {day.isLast && <span className="text-[10px] bg-gold/20 text-gold px-2 py-0.5 rounded-full font-bold">DEPARTURE</span>}
                </div>

                {/* Notes (arrival/departure/checkin) */}
                {day.notes.map((note, j) => (
                  <div key={`n-${j}`} className="flex items-center gap-2 py-1 text-sm text-brand-ink/70">
                    <div className="w-1.5 h-1.5 rounded-full bg-brand-ink/20 flex-shrink-0" />
                    {note}
                  </div>
                ))}

                {/* Activities */}
                {day.activities.map((a, j) => (
                  <div key={`a-${j}`} className="flex items-start justify-between py-1.5 group">
                    <div className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-teal mt-1.5 flex-shrink-0" />
                      <div>
                        <div className="text-sm font-medium text-brand-ink/85">{a.name}</div>
                        <div className="text-[11px] text-brand-ink/55">{a.duration} &middot; {a.category?.replace('_', ' ')}</div>
                      </div>
                    </div>
                    {a.price_for_two > 0 && <span className="text-xs text-gold flex-shrink-0">${a.price_for_two}</span>}
                  </div>
                ))}

                {/* Restaurants */}
                {day.restaurants.map((r, j) => (
                  <div key={`r-${j}`} className="flex items-start justify-between py-1.5 mt-1 border-t border-brand-ink/5">
                    <div className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-gold mt-1.5 flex-shrink-0" />
                      <div>
                        <div className="text-sm font-medium text-brand-ink/85">{r.name}</div>
                        <div className="text-[11px] text-brand-ink/55">{r.cuisine} &middot; {r.price_range}</div>
                      </div>
                    </div>
                    <span className="text-xs text-gold flex-shrink-0">${r.avg_meal_for_two}</span>
                  </div>
                ))}

                {/* Empty day suggestion */}
                {day.activities.length === 0 && day.restaurants.length === 0 && day.notes.length === 0 && (
                  <div className="text-xs text-brand-ink/45 italic py-1">Free day — explore on your own!</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
